import { type AnalysisBatchRes, TaskStatus } from '@dealstack/shared/lib/backendClient';
import {
	DATE_FORMATS,
	formatBEDateToUiWithFallback,
} from '@dealstack/shared/lib/format/dates/date';
import type { StatusTone } from '../primitives/primitives.types';
import { batchPath, categoryPath } from '../../workspacePaths.utils';
import type { BatchListItemView, BatchListState, BatchProgressView } from './batchList.types';

export const UNTITLED_BATCH_KEY = 'dealstack_ai.batch_list.untitled';
export const BATCH_COUNTS_KEY = 'dealstack_ai.batch_list.counts';
export const BATCH_PROGRESS_KEY = 'dealstack_ai.batch_list.progress';

const BATCH_STATE_KEYS: Record<BatchListState, string> = {
	queued: 'dealstack_ai.batch_list.state.queued',
	processing: 'dealstack_ai.batch_list.state.processing',
	ready: 'dealstack_ai.batch_list.state.ready',
	closed: 'dealstack_ai.batch_list.state.closed',
	failed: 'dealstack_ai.batch_list.state.failed',
	cancelled: 'dealstack_ai.batch_list.state.cancelled',
};

const BATCH_STATE_TONES: Record<BatchListState, StatusTone> = {
	queued: 'muted',
	processing: 'accent',
	ready: 'success',
	closed: 'neutral',
	failed: 'error',
	cancelled: 'muted',
};

const STATUS_STATES: Record<TaskStatus, BatchListState> = {
	[TaskStatus.PENDING]: 'queued',
	[TaskStatus.IN_PROGRESS]: 'processing',
	[TaskStatus.DONE]: 'ready',
	[TaskStatus.ERROR]: 'failed',
	[TaskStatus.CANCELLED]: 'cancelled',
};

/** A finished batch that was merged or abandoned reads as closed; every other reads as its task status. */
const batchListState = ({
	status,
	closedAt,
}: Pick<AnalysisBatchRes, 'status' | 'closedAt'>): BatchListState =>
	status === TaskStatus.DONE && closedAt !== null ? 'closed' : STATUS_STATES[status];

/**
 * A running or failed batch opens on its processing view (a failed one offers "Delete batch" there), a finished one on
 * its results; a cancelled batch does not open.
 */
export const batchOpenPath = (dealId: string, batch: AnalysisBatchRes): string | null => {
	switch (batchListState(batch)) {
		case 'queued':
		case 'processing':
		case 'failed':
			return batchPath(dealId, batch.id);
		case 'ready':
		case 'closed':
			return categoryPath(dealId, batch.id, 'documents');
		case 'cancelled':
			return null;
	}
};

/** A finished batch's review progress; a partial confirm leaves the rest open for a later session. */
const batchProgress = (
	state: BatchListState,
	{ progress }: Pick<AnalysisBatchRes, 'progress'>
): BatchProgressView | null =>
	state === 'ready' || state === 'closed'
		? {
				written: progress.written,
				discarded: progress.discarded,
				open: progress.open,
				held: progress.held.rows,
			}
		: null;

export const toBatchListItem = (dealId: string, batch: AnalysisBatchRes): BatchListItemView => {
	const state = batchListState(batch);
	return {
		batch,
		stateLabelKey: BATCH_STATE_KEYS[state],
		stateTone: BATCH_STATE_TONES[state],
		createdOn: formatBEDateToUiWithFallback(batch.createdAt, DATE_FORMATS.UI),
		path: batchOpenPath(dealId, batch),
		progress: batchProgress(state, batch),
	};
};

/** Case-insensitive match on the title as shown, "Untitled batch" included. */
export const matchesBatchSearch = (shownTitle: string, search: string): boolean => {
	const needle = search.trim().toLowerCase();
	return needle === '' || shownTitle.toLowerCase().includes(needle);
};
